/**
 * FluxNodeCard Component
 *
 * Displays detailed information about a single FluxNode
 * including status, tier, uptime, and reward information.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { FluxNode } from '../../types/fluxNode.types';
import {
  TIER_REQUIREMENTS,
  NODE_STATUS_COLORS
} from '../../types/fluxNode.types';
import { fluxNodeAPI } from '../../services/fluxNodeApi';
import type { ExtendedNetworkStats } from '../../services/fluxNodeApi';
import { Icons } from '../Icons';
import type { NodePurchaseInfo } from '../../pages/FluxNodeMonitor';
import type { DatavexExpirationMap } from '../../services/datavexApi';

interface FluxNodeCardProps {
  node: FluxNode;
  fluxPrice: number;
  onRefresh?: () => void;
  purchaseInfo?: NodePurchaseInfo;
  onUpdatePurchasePrice?: (price: number) => void;
  onUpdateTrackingStartDate?: (date: string | null) => void;
  onRemoveTracking?: () => void;
  extendedStats?: ExtendedNetworkStats;
  // Alias support
  alias?: string;
  onUpdateAlias?: (alias: string) => void;
  onRemoveAlias?: () => void;
  // View mode
  viewMode?: 'grid' | 'list';
  // Accordion behavior for list view (controlled from parent)
  isListExpanded?: boolean;
  onToggleListExpand?: () => void;
  // Datavex hosted IPs for badge display
  datavexHostedIps?: Set<string> | null;
  // Datavex expiration info for nodes
  datavexExpirations?: DatavexExpirationMap | null;
}

export function FluxNodeCard({ node, fluxPrice, onRefresh, purchaseInfo, onUpdatePurchasePrice, onUpdateTrackingStartDate, onRemoveTracking, extendedStats, alias, onUpdateAlias, onRemoveAlias, viewMode = 'grid', isListExpanded, onToggleListExpand, datavexHostedIps, datavexExpirations }: FluxNodeCardProps) {
  const { t } = useTranslation();
  // For grid view: internal state. For list view: controlled by parent (accordion behavior)
  const [isExpandedInternal, setIsExpandedInternal] = useState(false);

  // In list view, use controlled state from parent; in grid view, use internal state
  const isExpanded = viewMode === 'list' ? (isListExpanded ?? false) : isExpandedInternal;
  const toggleExpand = viewMode === 'list' && onToggleListExpand ? onToggleListExpand : () => setIsExpandedInternal(!isExpandedInternal);
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [priceInputValue, setPriceInputValue] = useState(purchaseInfo?.purchasePrice?.toString() || '');
  const [showAliasInput, setShowAliasInput] = useState(false);
  const [aliasInputValue, setAliasInputValue] = useState(alias || '');
  const [showDateInput, setShowDateInput] = useState(false);
  const [dateInputValue, setDateInputValue] = useState(purchaseInfo?.trackingStartDate || '');
  const [nodeInfo, setNodeInfo] = useState<{
    fluxVersion: string;
    cores: number;
    ram: number;
    ssd: number;
    country: string;
    countryCode: string;
    city: string;
    uptimeSeconds: number;
    downloadSpeed: number;
    uploadSpeed: number;
    cpuBench: number;
    ssdWrite: number;
  } | null>(null);
  const [nodeApps, setNodeApps] = useState<{ count: number; apps: Array<{ name: string; ports?: number[] }> } | null>(null);
  const [pnrData, setPnrData] = useState<{
    systemUptime: number;
    pnrEligible: boolean;
    uptimeFormatted: string;
    daysUntilEligible: number;
    uptimePercent: number;
  } | null>(null);
  const [isPnrLoading, setIsPnrLoading] = useState(false);
  const [latestBenchmarks, setLatestBenchmarks] = useState<{
    status: string;
    benchmarkTier: string;
    eps: number;
    ping: number;
    download: number;
    upload: number;
    cores: number;
    ram: number;
    ssd: number;
    hdd: number;
    ddWrite: number;
    totalStorage: number;
    disks: string;
    time: number;
  } | null>(null);

  // Fetch PNR data immediately on mount (lazy loading for badge display)
  useEffect(() => {
    if (!pnrData && !isPnrLoading) {
      setIsPnrLoading(true);
      fluxNodeAPI.getNodeSystemUptime(node.ip).then((data) => {
        if (data) {
          setPnrData(data);
        }
        setIsPnrLoading(false);
      }).catch(() => setIsPnrLoading(false));
    }
  }, [node.ip]); // Only depend on node.ip to avoid re-fetching

  // Fetch node info, apps, and benchmarks only when card is expanded
  useEffect(() => {
    if (isExpanded) {
      // Fetch extended node info
      if (!nodeInfo) {
        fluxNodeAPI.getNodeInfo(node.ip).then((info) => {
          if (info) {
            setNodeInfo({
              fluxVersion: info.fluxVersion,
              cores: info.cores,
              ram: info.ram,
              ssd: info.ssd,
              country: info.country,
              countryCode: info.countryCode,
              city: info.city,
              uptimeSeconds: info.uptimeSeconds,
              downloadSpeed: info.downloadSpeed,
              uploadSpeed: info.uploadSpeed,
              cpuBench: info.cpuBench,
              ssdWrite: info.ssdWrite
            });
          }
        });
      }
      // Fetch installed apps
      if (!nodeApps) {
        fluxNodeAPI.getNodeApps(node.ip).then((apps) => {
          if (apps) {
            setNodeApps(apps);
          }
        });
      }
      // Fetch latest benchmarks
      if (!latestBenchmarks) {
        fluxNodeAPI.getLatestBenchmarks(node.ip).then((benchmarks) => {
          if (benchmarks) {
            setLatestBenchmarks(benchmarks);
          }
        });
      }
    }
  }, [isExpanded, node.ip, nodeInfo, nodeApps, latestBenchmarks]);

  // Format uptime in human readable format
  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    if (days > 0) return `${days}j ${hours}h`;
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Get country flag emoji from country code
  const getCountryFlag = (countryCode: string): string => {
    if (!countryCode || countryCode.length !== 2) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  // Calculate metrics
  const status = fluxNodeAPI.getNodeStatus(node);
  const uptime = fluxNodeAPI.calculateUptime(node);

  // Use real network stats if available for accurate reward calculation
  const tierNodeCounts = extendedStats ? {
    CUMULUS: extendedStats.cumulusCount,
    NIMBUS: extendedStats.nimbusCount,
    STRATUS: extendedStats.stratusCount
  } : undefined;
  const rewards = fluxNodeAPI.calculateRewards(node, fluxPrice, tierNodeCounts);
  const tierInfo = TIER_REQUIREMENTS[node.tier];
  const statusColor = NODE_STATUS_COLORS[status];

  // Collateral is determined by tier (API doesn't return this field)
  const collateralFLUX = tierInfo.collateral;

  // Format IP address
  const ipParts = node.ip.split(':');
  const displayIP = ipParts[0];
  const port = ipParts[1] || '16127';

  // Calculate next reward estimate based on node rank in payment queue
  // Each rank position = 1 block = 30 seconds, so rank / 2 = minutes until payout
  // Reference: https://github.com/2ndtlmining/Fluxnode
  const minutesUntilReward = node.rank / 2;

  // Format next reward time in a readable way
  const formatNextRewardTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.round(minutes % 60);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}j ${remainingHours}h`;
    }
    return `${hours}h ${remainingMinutes}m`;
  };

  const nextRewardFormatted = formatNextRewardTime(minutesUntilReward);

  // Calculate blocks since last paid (for extended details)
  const blocksSinceLastPaid = node.last_confirmed_height - node.last_paid_height;

  // Calculate maintenance window
  const maintenanceWindow = extendedStats?.blockHeight
    ? fluxNodeAPI.calculateMaintenanceWindow(node.last_confirmed_height, extendedStats.blockHeight)
    : null;

  // Calculate gain/loss if purchase info is available
  const hasTracking = purchaseInfo && purchaseInfo.purchasePrice > 0;
  const nodeGainLoss = hasTracking && fluxPrice > 0 ? {
    invested: collateralFLUX * purchaseInfo.purchasePrice,
    currentValue: collateralFLUX * fluxPrice,
    gainLossUSD: collateralFLUX * (fluxPrice - purchaseInfo.purchasePrice),
    gainLossPercent: ((fluxPrice - purchaseInfo.purchasePrice) / purchaseInfo.purchasePrice) * 100
  } : null;

  // Calculate payback/ROI progress if purchase info is available
  const paybackProgress = hasTracking && fluxPrice > 0 && rewards.monthly > 0 ? (() => {
    const investedUSD = collateralFLUX * purchaseInfo.purchasePrice;
    const monthlyRewardsUSD = rewards.monthly * fluxPrice;
    const paybackMonths = investedUSD / monthlyRewardsUSD;

    // Use custom tracking start date if available, otherwise fall back to PNR uptime
    let uptimeDays: number;
    let hasCustomDate = false;

    if (purchaseInfo.trackingStartDate) {
      // Calculate days since custom start date
      const startDate = new Date(purchaseInfo.trackingStartDate);
      const now = new Date();
      uptimeDays = Math.max(0, (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      hasCustomDate = true;
    } else {
      // Fall back to PNR system uptime
      uptimeDays = pnrData?.systemUptime ? pnrData.systemUptime / 86400 : 0;
    }

    const uptimeMonths = uptimeDays / 30;
    const estimatedRewardsEarned = uptimeMonths * monthlyRewardsUSD;
    const progressPercent = Math.min((estimatedRewardsEarned / investedUSD) * 100, 100);
    const isPaidOff = progressPercent >= 100;
    const monthsRemaining = Math.max(0, paybackMonths - uptimeMonths);

    return {
      investedUSD,
      monthlyRewardsUSD,
      paybackMonths,
      uptimeDays,
      uptimeMonths,
      estimatedRewardsEarned,
      progressPercent,
      isPaidOff,
      monthsRemaining,
      hasCustomDate,
      trackingStartDate: purchaseInfo.trackingStartDate,
      // Format payback time
      paybackFormatted: paybackMonths < 12
        ? `${Math.round(paybackMonths)} mois`
        : `${(paybackMonths / 12).toFixed(1)} ans`,
      remainingFormatted: monthsRemaining < 12
        ? `${Math.round(monthsRemaining)} mois`
        : `${(monthsRemaining / 12).toFixed(1)} ans`
    };
  })() : null;

  // Check if node is hosted by Datavex
  const isDatavexHosted = datavexHostedIps?.has(displayIP) ?? false;
  // Get expiration info for Datavex-hosted nodes
  const datavexExpiration = isDatavexHosted && datavexExpirations?.get(displayIP);

  // Handle saving purchase price
  const handleSavePurchasePrice = () => {
    const price = parseFloat(priceInputValue);
    if (!isNaN(price) && price > 0 && onUpdatePurchasePrice) {
      onUpdatePurchasePrice(price);
      setShowPriceInput(false);
    }
  };

  // Handle saving alias
  const handleSaveAlias = () => {
    if (onUpdateAlias) {
      onUpdateAlias(aliasInputValue);
      setShowAliasInput(false);
    }
  };

  // Compact list row view
  if (viewMode === 'list') {
    return (
      <div
        className="glass-card"
        style={{
          border: `1px solid ${tierInfo.color}`,
          borderLeft: `4px solid ${tierInfo.color}`,
          transition: 'all 0.2s',
          overflow: 'hidden'
        }}
      >
        {/* Main row */}
        <div
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}
          onClick={toggleExpand}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '';
          }}
        >
          {/* Expand indicator */}
          <div style={{ minWidth: '20px', textAlign: 'center' }}>
            <Icons.ChevronRight
              size={16}
              style={{
                color: 'var(--muted)',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            />
          </div>

          {/* Name/Alias + Tier + IP */}
          <div style={{ minWidth: '200px', flex: '1 1 200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
              {alias ? (
                <span
                  style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', cursor: 'pointer' }}
                  onClick={(e) => { e.stopPropagation(); setShowAliasInput(true); }}
                >
                  {alias}
                </span>
              ) : onUpdateAlias ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAliasInput(true); }}
                  style={{
                    padding: '2px 6px',
                    fontSize: '10px',
                    color: 'var(--muted)',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + {t('fluxNode.nodeCard.addAlias', 'Name')}
                </button>
              ) : null}
              <span style={{ fontSize: '12px', fontWeight: 600, color: tierInfo.color }}>
                {node.tier}
              </span>
              <div
                style={{
                  padding: '1px 6px',
                  borderRadius: '8px',
                  background: statusColor,
                  color: 'white',
                  fontSize: '9px',
                  fontWeight: 600
                }}
              >
                {status}
              </div>
              {/* PNR Badge in list view */}
              {isPnrLoading ? (
                <div
                  style={{
                    padding: '1px 6px',
                    borderRadius: '8px',
                    background: 'rgba(107, 114, 128, 0.3)',
                    color: 'var(--muted)',
                    fontSize: '9px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                >
                  <Icons.Loader2 size={9} style={{ animation: 'spin 1s linear infinite' }} />
                  PNR
                </div>
              ) : pnrData && (
                <div
                  style={{
                    padding: '1px 6px',
                    borderRadius: '8px',
                    background: pnrData.pnrEligible
                      ? 'linear-gradient(135deg, #10b981, #059669)'
                      : 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title={pnrData.pnrEligible
                    ? t('fluxNode.pnr.eligibleTooltip', 'Éligible aux Progressive Node Rewards')
                    : t('fluxNode.pnr.notEligibleTooltip', `${pnrData.daysUntilEligible} jours restants`)
                  }
                >
                  <Icons.Award size={9} />
                  PNR {pnrData.pnrEligible ? '✓' : `${pnrData.uptimePercent}%`}
                </div>
              )}
              {/* Datavex Hosted Badge in list view */}
              {isDatavexHosted && (
                <div
                  style={{
                    padding: '1px 6px',
                    borderRadius: '8px',
                    background: datavexExpiration?.isExpiringSoon
                      ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                      : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title={datavexExpiration
                    ? t('fluxNode.datavex.expiresIn', 'Expire dans {{time}}', { time: datavexExpiration.formatted })
                    : t('fluxNode.datavex.hostedTooltip', 'Hébergé par Datavex')
                  }
                >
                  <Icons.Server size={9} />
                  {datavexExpiration ? datavexExpiration.formatted : 'Datavex'}
                </div>
              )}
              {/* Maintenance Window Badge in list view */}
              {maintenanceWindow && (
                <div
                  style={{
                    padding: '1px 6px',
                    borderRadius: '8px',
                    background: maintenanceWindow.status === 'closed'
                      ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                      : 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                  title={maintenanceWindow.status === 'closed'
                    ? t('fluxNode.maintenance.closedTooltip', 'Fenêtre de maintenance fermée - ne pas effectuer de maintenance')
                    : t('fluxNode.maintenance.openTooltip', 'Temps restant avant fermeture de la fenêtre de maintenance')
                  }
                >
                  <Icons.Wrench size={9} />
                  {maintenanceWindow.formatted}
                </div>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', fontFamily: 'monospace' }}>
              {displayIP}:{port}
            </div>
            {/* Alias edit inline */}
            {showAliasInput && (
              <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }} onClick={(e) => e.stopPropagation()}>
                <input
                  type="text"
                  value={aliasInputValue}
                  onChange={(e) => setAliasInputValue(e.target.value)}
                  placeholder={t('fluxNode.nodeCard.aliasPlaceholder', 'Node name...')}
                  style={{
                    padding: '4px 8px',
                    fontSize: '12px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    width: '120px'
                  }}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSaveAlias(); }}
                  autoFocus
                />
                <button onClick={handleSaveAlias} style={{ padding: '4px 8px', fontSize: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>OK</button>
                <button onClick={() => { setShowAliasInput(false); setAliasInputValue(alias || ''); }} style={{ padding: '4px 8px', fontSize: '10px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>✕</button>
              </div>
            )}
          </div>

          {/* Rank */}
          <div style={{ minWidth: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.rank')}</div>
            <div style={{ fontSize: '14px', fontWeight: 700 }}>#{node.rank}</div>
          </div>

          {/* Uptime */}
          <div style={{ minWidth: '80px', textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.uptime')}</div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: uptime >= 95 ? '#10b981' : uptime >= 85 ? '#f59e0b' : '#ef4444' }}>
              {uptime.toFixed(1)}%
            </div>
          </div>

          {/* Collateral */}
          <div style={{ minWidth: '100px', textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.collateral')}</div>
            <div style={{ fontSize: '13px', fontWeight: 600 }}>{collateralFLUX.toLocaleString()} FLUX</div>
          </div>

          {/* Monthly Rewards */}
          <div style={{ minWidth: '120px', textAlign: 'center' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.monthlyRewards')}</div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#6366f1' }}>{rewards.monthly.toFixed(0)} FLUX</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>${(rewards.monthly * fluxPrice).toFixed(0)}</div>
          </div>

          {/* Purchase Price / Gain-Loss */}
          <div style={{ minWidth: '120px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            {showPriceInput ? (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <input
                  type="number"
                  step="0.0001"
                  placeholder="$/FLUX"
                  value={priceInputValue}
                  onChange={(e) => setPriceInputValue(e.target.value)}
                  style={{
                    padding: '4px 6px',
                    fontSize: '11px',
                    background: 'var(--card-bg)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    width: '70px'
                  }}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSavePurchasePrice(); }}
                />
                <button onClick={handleSavePurchasePrice} style={{ padding: '4px 6px', fontSize: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>OK</button>
              </div>
            ) : nodeGainLoss ? (
              <div>
                <div style={{ fontSize: '10px', color: 'var(--muted)' }}>@${purchaseInfo?.purchasePrice?.toFixed(4)}</div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 700,
                  color: nodeGainLoss.gainLossUSD >= 0 ? '#10b981' : '#ef4444'
                }}>
                  {nodeGainLoss.gainLossUSD >= 0 ? '+' : ''}{nodeGainLoss.gainLossPercent.toFixed(1)}%
                </div>
              </div>
            ) : onUpdatePurchasePrice ? (
              <button
                onClick={() => setShowPriceInput(true)}
                style={{
                  padding: '4px 8px',
                  fontSize: '10px',
                  color: 'var(--muted)',
                  background: 'var(--border)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                + {t('fluxNode.nodeCard.price', 'Price')}
              </button>
            ) : null}
          </div>

          {/* ROI */}
          <div style={{ minWidth: '70px', textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.roi')}</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#10b981' }}>{rewards.roi.toFixed(1)}%</div>
          </div>

          {/* Next Reward */}
          <div style={{ minWidth: '80px', textAlign: 'right' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.nextReward', 'Next')}</div>
            <div style={{ fontSize: '12px', fontWeight: 600 }}>{nextRewardFormatted}</div>
          </div>
        </div>

        {/* Expanded details section */}
        {isExpanded && (
          <div
            style={{
              padding: '16px',
              background: 'rgba(99, 102, 241, 0.02)',
              borderTop: '1px solid var(--border)'
            }}
          >
            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              {/* Daily Rewards */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.dailyRewards')}</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{rewards.daily.toFixed(2)} FLUX</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)' }}>${(rewards.daily * fluxPrice).toFixed(2)}</div>
              </div>

              {/* Weekly Rewards */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.weeklyRewards')}</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{rewards.weekly.toFixed(2)} FLUX</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)' }}>${(rewards.weekly * fluxPrice).toFixed(2)}</div>
              </div>

              {/* Yearly Rewards */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.yearlyRewards')}</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{rewards.yearly.toFixed(0)} FLUX</div>
                <div style={{ fontSize: '10px', color: 'var(--muted)' }}>${(rewards.yearly * fluxPrice).toFixed(0)}</div>
              </div>

              {/* Blocks Since Paid */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.blocksSincePaid')}</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>{blocksSinceLastPaid.toLocaleString()}</div>
              </div>

              {/* Collateral Value */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.collateral')} USD</div>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>${(collateralFLUX * fluxPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              </div>

              {/* Gain/Loss if tracking */}
              {nodeGainLoss && (
                <div style={{
                  padding: '12px',
                  background: nodeGainLoss.gainLossUSD >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px'
                }}>
                  <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>{t('fluxNode.nodeCard.collateralGainLoss')}</div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: nodeGainLoss.gainLossUSD >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {nodeGainLoss.gainLossUSD >= 0 ? '+' : ''}${nodeGainLoss.gainLossUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
              )}
            </div>

            {/* Payback Progress + Latest Benchmarks Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
              {/* Payback Progress Section */}
              {paybackProgress && (
                <div
                  style={{
                    padding: '14px',
                    background: paybackProgress.isPaidOff
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))'
                      : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1))',
                    border: `1px solid ${paybackProgress.isPaidOff ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
                    borderRadius: '10px'
                  }}
                >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Icons.TrendingUp size={18} style={{ color: paybackProgress.isPaidOff ? '#10b981' : '#6366f1' }} />
                  <span style={{ fontSize: '13px', fontWeight: 700, color: paybackProgress.isPaidOff ? '#10b981' : '#6366f1' }}>
                    {t('fluxNode.nodeCard.paybackProgress', 'Payback Progress')}
                  </span>
                  {paybackProgress.isPaidOff && (
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: '#10b981',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 700
                    }}>
                      {t('fluxNode.nodeCard.paidOff', 'PAID OFF')}
                    </span>
                  )}
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    width: '100%',
                    height: '10px',
                    background: 'var(--border)',
                    borderRadius: '5px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${paybackProgress.progressPercent}%`,
                      height: '100%',
                      background: paybackProgress.isPaidOff
                        ? 'linear-gradient(90deg, #10b981, #059669)'
                        : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                      borderRadius: '5px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: 'var(--muted)' }}>
                    <span>{paybackProgress.progressPercent.toFixed(1)}% {t('fluxNode.nodeCard.recovered', 'recovered')}</span>
                    <span>${paybackProgress.investedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>

                {/* Stats Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', fontSize: '11px' }}>
                  <div>
                    <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>{t('fluxNode.nodeCard.invested', 'Invested')}</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>${paybackProgress.investedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>@${purchaseInfo?.purchasePrice?.toFixed(4)}/FLUX</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>{t('fluxNode.nodeCard.monthlyRewardsUSD', 'Monthly Rewards')}</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#10b981' }}>+${paybackProgress.monthlyRewardsUSD.toFixed(0)}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{rewards.monthly.toFixed(1)} FLUX</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>{t('fluxNode.nodeCard.estimatedEarned', 'Est. Earned')}</div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: '#8b5cf6' }}>~${paybackProgress.estimatedRewardsEarned.toFixed(0)}</div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>{Math.round(paybackProgress.uptimeDays)} {t('fluxNode.nodeCard.daysRunning', 'days running')}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>
                      {paybackProgress.isPaidOff ? t('fluxNode.nodeCard.paybackTime', 'Payback Time') : t('fluxNode.nodeCard.remaining', 'Remaining')}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: paybackProgress.isPaidOff ? '#10b981' : 'var(--text)'
                    }}>
                      {paybackProgress.isPaidOff ? paybackProgress.paybackFormatted : paybackProgress.remainingFormatted}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                      {t('fluxNode.nodeCard.totalPayback', 'Total')}: {paybackProgress.paybackFormatted}
                    </div>
                  </div>
                </div>

                {/* Tracking Start Date */}
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(99, 102, 241, 0.2)' }} onClick={(e) => e.stopPropagation()}>
                  {showDateInput ? (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <input
                        type="date"
                        value={dateInputValue}
                        onChange={(e) => setDateInputValue(e.target.value)}
                        style={{
                          padding: '6px 10px',
                          fontSize: '11px',
                          background: 'var(--card-bg)',
                          color: 'var(--text)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          flex: 1,
                          minWidth: '120px'
                        }}
                      />
                      <button
                        onClick={() => {
                          if (dateInputValue && onUpdateTrackingStartDate) {
                            onUpdateTrackingStartDate(dateInputValue);
                          }
                          setShowDateInput(false);
                        }}
                        style={{
                          padding: '6px 10px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        OK
                      </button>
                      {paybackProgress.hasCustomDate && (
                        <button
                          onClick={() => {
                            if (onUpdateTrackingStartDate) {
                              onUpdateTrackingStartDate(null);
                              setDateInputValue('');
                            }
                            setShowDateInput(false);
                          }}
                          style={{
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer'
                          }}
                        >
                          {t('common.reset', 'Reset')}
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowDateInput(false);
                          setDateInputValue(purchaseInfo?.trackingStartDate || '');
                        }}
                        style={{
                          padding: '6px 10px',
                          fontSize: '10px',
                          fontWeight: 600,
                          background: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '10px' }}>
                      <Icons.Calendar size={12} style={{ color: 'var(--muted)' }} />
                      <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.trackingSince', 'Tracking since')}:</span>
                      {paybackProgress.hasCustomDate ? (
                        <button
                          onClick={() => setShowDateInput(true)}
                          style={{
                            padding: '2px 6px',
                            background: 'rgba(99, 102, 241, 0.2)',
                            color: '#6366f1',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {new Date(paybackProgress.trackingStartDate!).toLocaleDateString()}
                        </button>
                      ) : (
                        <>
                          <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                            {t('fluxNode.nodeCard.pnrUptime', 'PNR uptime')}
                          </span>
                          {onUpdateTrackingStartDate && (
                            <button
                              onClick={() => setShowDateInput(true)}
                              style={{
                                padding: '2px 6px',
                                background: 'var(--border)',
                                color: 'var(--muted)',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '9px',
                                cursor: 'pointer'
                              }}
                            >
                              {t('fluxNode.nodeCard.setDate', 'Set date')}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

              {/* Latest Benchmarks - in same grid row as Payback */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px', fontSize: '11px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icons.Activity size={14} />
                  {t('fluxNode.nodeCard.latestBenchmarks', 'Latest Benchmarks')}
                  {latestBenchmarks?.benchmarkTier && (
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'rgba(99, 102, 241, 0.2)',
                      color: '#6366f1',
                      fontSize: '9px',
                      fontWeight: 700
                    }}>
                      {latestBenchmarks.benchmarkTier}
                    </span>
                  )}
                </div>
                {latestBenchmarks ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', fontSize: '10px' }}>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.eps', 'EPS')}:</span>{' '}
                        <span style={{ color: '#6366f1', fontWeight: 600 }}>{latestBenchmarks.eps.toLocaleString()}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>Ping:</span>{' '}
                        <span style={{ fontWeight: 600 }}>{latestBenchmarks.ping > 0 ? `${latestBenchmarks.ping} ms` : '-'}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>↓</span>{' '}
                        <span style={{ fontWeight: 600 }}>{(latestBenchmarks.download > 0 ? Math.round(latestBenchmarks.download) : nodeInfo?.downloadSpeed && nodeInfo.downloadSpeed > 0 ? Math.round(nodeInfo.downloadSpeed) : 0) > 0 ? `${latestBenchmarks.download > 0 ? Math.round(latestBenchmarks.download) : Math.round(nodeInfo?.downloadSpeed || 0)} Mbps` : '-'}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>↑</span>{' '}
                        <span style={{ fontWeight: 600 }}>{(latestBenchmarks.upload > 0 ? Math.round(latestBenchmarks.upload) : nodeInfo?.uploadSpeed && nodeInfo.uploadSpeed > 0 ? Math.round(nodeInfo.uploadSpeed) : 0) > 0 ? `${latestBenchmarks.upload > 0 ? Math.round(latestBenchmarks.upload) : Math.round(nodeInfo?.uploadSpeed || 0)} Mbps` : '-'}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.diskWrite', 'Disk Write')}:</span>{' '}
                        <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{latestBenchmarks.ddWrite > 0 ? `${Math.round(latestBenchmarks.ddWrite)} MB/s` : '-'}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.totalStorage', 'Storage')}:</span>{' '}
                        <span style={{ fontWeight: 600 }}>{latestBenchmarks.totalStorage > 0 ? `${latestBenchmarks.totalStorage} GB` : '-'}</span>
                      </div>
                    </div>
                    {latestBenchmarks.time > 0 && (
                      <div style={{ marginTop: '6px', fontSize: '9px', color: 'var(--muted)' }}>
                        {t('fluxNode.nodeCard.benchmarkTime', 'Last benchmark')}: {new Date(latestBenchmarks.time * 1000).toLocaleString()}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ color: 'var(--muted)' }}>{t('common.loading', 'Loading...')}</div>
                )}
              </div>
            </div>

            {/* Node Info & Hardware */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginTop: '16px' }}>
              {/* Hardware & Location */}
              <div style={{ padding: '12px', background: 'var(--border)', borderRadius: '8px', fontSize: '11px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icons.Server size={14} />
                  {t('fluxNode.nodeCard.hardwareLocation', 'Hardware & Location')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: 'var(--muted)' }}>FluxOS:</span>
                  <span style={{ color: '#10b981', fontWeight: 600 }}>{nodeInfo?.fluxVersion || 'Loading...'}</span>
                </div>
                {nodeInfo?.countryCode && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--muted)' }}>Location:</span>
                    <span>{getCountryFlag(nodeInfo.countryCode)} {nodeInfo.city ? `${nodeInfo.city}, ` : ''}{nodeInfo.country}</span>
                  </div>
                )}
                {nodeInfo && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '8px' }}>
                      <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#6366f1' }}>{nodeInfo.cores}</div>
                        <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.cores', 'Cores')}</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#8b5cf6' }}>{nodeInfo.ram} GB</div>
                        <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.ram', 'RAM')}</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(236, 72, 153, 0.1)', borderRadius: '6px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 700, color: '#ec4899' }}>{nodeInfo.ssd} GB</div>
                        <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.ssd', 'SSD')}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '10px' }}>
                      <span><span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.uptime', 'Uptime')}:</span> <span style={{ color: '#10b981' }}>{nodeInfo.uptimeSeconds > 0 ? formatUptime(nodeInfo.uptimeSeconds) : '-'}</span></span>
                    </div>
                  </>
                )}
              </div>

              {/* PNR Status */}
              <div
                style={{
                  padding: '12px',
                  background: pnrData?.pnrEligible ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                  border: `1px solid ${pnrData?.pnrEligible ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                  borderRadius: '8px',
                  fontSize: '11px'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px', color: pnrData?.pnrEligible ? '#10b981' : '#f59e0b' }}>
                  <Icons.Award size={14} />
                  {t('fluxNode.pnr.title', 'Progressive Node Rewards (PNR)')}
                </div>
                {isPnrLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--muted)' }}>
                    <Icons.Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    {t('fluxNode.pnr.loading', 'Chargement...')}
                  </div>
                ) : pnrData ? (
                  <>
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ color: 'var(--muted)' }}>{t('fluxNode.pnr.systemUptime', 'Uptime Système')}:</span>
                        <span style={{ fontWeight: 600 }}>{pnrData.uptimeFormatted}</span>
                      </div>
                      <div style={{ width: '100%', height: '6px', background: 'var(--border)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${pnrData.uptimePercent}%`,
                          height: '100%',
                          background: pnrData.pnrEligible ? 'linear-gradient(90deg, #10b981, #059669)' : 'linear-gradient(90deg, #f59e0b, #d97706)',
                          borderRadius: '3px'
                        }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: 'var(--muted)' }}>
                        <span>{pnrData.uptimePercent}%</span>
                        <span>30 {t('fluxNode.pnr.days', 'jours')}</span>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px',
                      background: pnrData.pnrEligible ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.2)',
                      borderRadius: '4px'
                    }}>
                      {pnrData.pnrEligible ? (
                        <><Icons.CheckCircle size={14} style={{ color: '#10b981' }} /><span style={{ color: '#10b981', fontWeight: 600 }}>{t('fluxNode.pnr.eligible', 'Éligible PNR')}</span></>
                      ) : (
                        <><Icons.Clock size={14} style={{ color: '#f59e0b' }} /><span style={{ color: '#f59e0b', fontWeight: 600 }}>{pnrData.daysUntilEligible} {t('fluxNode.pnr.daysRemaining', 'jours restants')}</span></>
                      )}
                    </div>
                  </>
                ) : (
                  <div style={{ color: 'var(--muted)' }}>{t('fluxNode.pnr.unavailable', 'Données non disponibles')}</div>
                )}
              </div>
            </div>

            {/* Apps Section */}
            <div style={{ marginTop: '16px', padding: '12px', background: 'var(--border)', borderRadius: '8px', fontSize: '11px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icons.Box size={14} />
                {t('fluxNode.nodeCard.hostedApps', 'Hosted Apps')}: <span style={{ color: '#8b5cf6' }}>{nodeApps ? nodeApps.count : '...'}</span>
              </div>
              {nodeApps && nodeApps.count > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {nodeApps.apps.slice(0, 8).map((app, idx) => (
                    <a
                      key={idx}
                      href={fluxNodeAPI.buildAppUrl(app.name, node.ip.split(':')[0])}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        borderRadius: '4px',
                        textDecoration: 'none',
                        fontSize: '10px'
                      }}
                    >
                      {app.name}
                    </a>
                  ))}
                  {nodeApps.count > 8 && (
                    <span style={{ padding: '4px 8px', color: 'var(--muted)', fontSize: '10px' }}>+{nodeApps.count - 8} {t('fluxNode.nodeCard.more', 'more')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Technical Details */}
            <div style={{ marginTop: '16px', padding: '12px', background: 'var(--border)', borderRadius: '8px', fontSize: '10px', fontFamily: 'monospace' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                <div><span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.txHash')}:</span> {node.txhash.substring(0, 16)}...</div>
                <div><span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.paymentAddress')}:</span> {node.payment_address.substring(0, 16)}...</div>
                <div><span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.confirmedHeight')}:</span> {node.confirmed_height.toLocaleString()}</div>
                <div><span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.lastPaidHeight')}:</span> {node.last_paid_height.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Grid card view (default)
  return (
    <div
      className="glass-card"
      style={{
        padding: '20px',
        border: `2px solid ${tierInfo.color}`,
        transition: 'all 0.2s',
        cursor: 'pointer'
      }}
      onClick={toggleExpand}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
        <div>
          {/* Alias Display/Edit */}
          {onUpdateAlias && (
            <div style={{ marginBottom: '8px' }} onClick={(e) => e.stopPropagation()}>
              {showAliasInput ? (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder={t('fluxNode.nodeCard.aliasPlaceholder', 'Node name...')}
                    value={aliasInputValue}
                    onChange={(e) => setAliasInputValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      fontSize: '13px',
                      background: 'var(--card-bg)',
                      color: 'var(--text)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      minWidth: '120px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleSaveAlias();
                    }}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveAlias}
                    style={{
                      padding: '6px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    OK
                  </button>
                  <button
                    onClick={() => { setShowAliasInput(false); setAliasInputValue(alias || ''); }}
                    style={{
                      padding: '6px 10px',
                      fontSize: '11px',
                      fontWeight: 600,
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : alias ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer'
                  }}
                  onClick={(e) => { e.stopPropagation(); setShowAliasInput(true); }}
                >
                  <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>
                    {alias}
                  </span>
                  <Icons.Edit size={12} style={{ color: 'var(--muted)', opacity: 0.6 }} />
                </div>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowAliasInput(true); }}
                  style={{
                    padding: '4px 8px',
                    fontSize: '11px',
                    color: 'var(--muted)',
                    background: 'var(--border)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Icons.Edit size={12} />
                  {t('fluxNode.nodeCard.addAlias', 'Add name')}
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: tierInfo.color }}>
              {node.tier}
            </h3>
            <div
              style={{
                padding: '2px 8px',
                borderRadius: '12px',
                background: statusColor,
                color: 'white',
                fontSize: '10px',
                fontWeight: 600
              }}
            >
              {status}
            </div>
            {/* PNR Eligibility Badge */}
            {isPnrLoading ? (
              <div
                style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #6b7280, #4b5563)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={t('fluxNode.pnr.loading', 'Chargement PNR...')}
              >
                <Icons.Loader2 size={10} style={{ animation: 'spin 1s linear infinite' }} />
                PNR
              </div>
            ) : pnrData && (
              <div
                style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: pnrData.pnrEligible
                    ? 'linear-gradient(135deg, #10b981, #059669)'
                    : 'linear-gradient(135deg, #f59e0b, #d97706)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={pnrData.pnrEligible
                  ? t('fluxNode.pnr.eligibleTooltip', 'Éligible aux Progressive Node Rewards')
                  : t('fluxNode.pnr.notEligibleTooltip', `${pnrData.daysUntilEligible} jours restants pour l'éligibilité PNR`)
                }
              >
                <Icons.Award size={10} />
                PNR {pnrData.pnrEligible ? '✓' : `${pnrData.uptimePercent}%`}
              </div>
            )}
            {/* Datavex Hosted Badge */}
            {isDatavexHosted && (
              <div
                style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: datavexExpiration?.isExpiringSoon
                    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                    : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={datavexExpiration
                  ? t('fluxNode.datavex.expiresIn', 'Expire dans {{time}}', { time: datavexExpiration.formatted })
                  : t('fluxNode.datavex.hostedTooltip', 'Hébergé par Datavex')
                }
              >
                <Icons.Server size={10} />
                {datavexExpiration ? datavexExpiration.formatted : 'Datavex'}
              </div>
            )}
            {/* Maintenance Window Badge */}
            {maintenanceWindow && (
              <div
                style={{
                  padding: '2px 8px',
                  borderRadius: '12px',
                  background: maintenanceWindow.status === 'closed'
                    ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                title={maintenanceWindow.status === 'closed'
                  ? t('fluxNode.maintenance.closedTooltip', 'Fenêtre de maintenance fermée - ne pas effectuer de maintenance')
                  : t('fluxNode.maintenance.openTooltip', 'Temps restant avant fermeture de la fenêtre de maintenance')
                }
              >
                <Icons.Wrench size={10} />
                {maintenanceWindow.formatted}
              </div>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--muted)', fontFamily: 'monospace' }}>
            {displayIP}:{port}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '2px' }}>
            {t('fluxNode.nodeCard.rank')}
          </div>
          <div style={{ fontSize: '20px', fontWeight: 700 }}>
            #{node.rank}
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
        {/* Uptime */}
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('fluxNode.nodeCard.uptime')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              style={{
                width: '100%',
                height: '6px',
                background: 'var(--border)',
                borderRadius: '3px',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  width: `${uptime}%`,
                  height: '100%',
                  background: uptime >= 95 ? '#10b981' : uptime >= 85 ? '#f59e0b' : '#ef4444',
                  transition: 'width 0.3s'
                }}
              />
            </div>
            <span style={{ fontSize: '12px', fontWeight: 600, minWidth: '45px' }}>
              {uptime.toFixed(1)}%
            </span>
          </div>
        </div>

        {/* Collateral */}
        <div>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('fluxNode.nodeCard.collateral')}
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {collateralFLUX.toLocaleString()} FLUX
          </div>
          <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
            ${(collateralFLUX * fluxPrice).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      {/* Rewards Section */}
      <div
        style={{
          padding: '12px',
          background: 'rgba(99, 102, 241, 0.1)',
          border: '1px solid rgba(99, 102, 241, 0.3)',
          borderRadius: '8px',
          marginBottom: '12px'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
              {t('fluxNode.nodeCard.monthlyRewards')}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#6366f1' }}>
              {rewards.monthly.toFixed(2)} FLUX
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              ${(rewards.monthly * fluxPrice).toFixed(2)} USD
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '2px' }}>
              {t('fluxNode.nodeCard.roi')}
            </div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#10b981' }}>
              {rewards.roi.toFixed(1)}%
            </div>
            <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
              {t('fluxNode.nodeCard.perYear')}
            </div>
          </div>
        </div>
      </div>

      {/* Next Reward Countdown */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Icons.Clock size={14} style={{ color: 'var(--muted)' }} />
        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('fluxNode.nodeCard.nextRewardIn')}{nextRewardFormatted}
        </span>
      </div>

      {/* Purchase Price / Gain-Loss Section */}
      {onUpdatePurchasePrice && (
        <div
          style={{
            padding: '12px',
            background: nodeGainLoss
              ? (nodeGainLoss.gainLossUSD >= 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)')
              : 'rgba(107, 114, 128, 0.1)',
            border: `1px solid ${nodeGainLoss
              ? (nodeGainLoss.gainLossUSD >= 0 ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)')
              : 'rgba(107, 114, 128, 0.3)'}`,
            borderRadius: '8px',
            marginBottom: '12px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {showPriceInput ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                step="0.0001"
                placeholder={t('fluxNode.nodeCard.purchasePricePlaceholder')}
                value={priceInputValue}
                onChange={(e) => setPriceInputValue(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  flex: 1,
                  padding: '8px',
                  fontSize: '12px',
                  background: 'var(--card-bg)',
                  color: 'var(--text)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSavePurchasePrice();
                }}
              />
              <button
                onClick={handleSavePurchasePrice}
                style={{
                  padding: '8px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
              <button
                onClick={() => setShowPriceInput(false)}
                style={{
                  padding: '8px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>
          ) : nodeGainLoss ? (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
                  {t('fluxNode.nodeCard.purchasePrice')}: ${purchaseInfo?.purchasePrice?.toFixed(4)}/FLUX
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button
                    onClick={() => setShowPriceInput(true)}
                    style={{
                      padding: '2px 6px',
                      fontSize: '10px',
                      background: 'var(--border)',
                      color: 'var(--text)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  {onRemoveTracking && (
                    <button
                      onClick={onRemoveTracking}
                      style={{
                        padding: '2px 6px',
                        fontSize: '10px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.collateralGainLoss')}</div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: nodeGainLoss.gainLossUSD >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {nodeGainLoss.gainLossUSD >= 0 ? '+' : ''}${nodeGainLoss.gainLossUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.variation')}</div>
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: nodeGainLoss.gainLossPercent >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {nodeGainLoss.gainLossPercent >= 0 ? '+' : ''}{nodeGainLoss.gainLossPercent.toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowPriceInput(true)}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--muted)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px'
              }}
            >
              <Icons.DollarSign size={14} />
              {t('fluxNode.nodeCard.enterPurchasePrice')}
            </button>
          )}
        </div>
      )}

      {/* Expand/Collapse Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggleExpand();
        }}
        style={{
          width: '100%',
          padding: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: 'var(--accent)',
          background: 'none',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--border)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'none';
        }}
      >
        {isExpanded ? `▼ ${t('fluxNode.nodeCard.showLess')}` : `▶ ${t('fluxNode.nodeCard.showMore')}`}
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div
          style={{
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border)'
          }}
        >
          {/* Detailed Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.nodeCard.dailyRewards')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {rewards.daily.toFixed(2)} FLUX
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.nodeCard.weeklyRewards')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {rewards.weekly.toFixed(2)} FLUX
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.nodeCard.yearlyRewards')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {rewards.yearly.toFixed(2)} FLUX
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
                {t('fluxNode.nodeCard.blocksSincePaid')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {blocksSinceLastPaid.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Payback Progress Section */}
          {paybackProgress && (
            <div
              style={{
                padding: '14px',
                background: paybackProgress.isPaidOff
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))'
                  : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1))',
                border: `1px solid ${paybackProgress.isPaidOff ? 'rgba(16, 185, 129, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
                borderRadius: '10px',
                marginBottom: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <Icons.TrendingUp size={16} style={{ color: paybackProgress.isPaidOff ? '#10b981' : '#6366f1' }} />
                <span style={{ fontSize: '12px', fontWeight: 700, color: paybackProgress.isPaidOff ? '#10b981' : '#6366f1' }}>
                  {t('fluxNode.nodeCard.paybackProgress', 'Payback Progress')}
                </span>
                {paybackProgress.isPaidOff && (
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '10px',
                    background: '#10b981',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 700
                  }}>
                    {t('fluxNode.nodeCard.paidOff', 'PAID OFF')}
                  </span>
                )}
              </div>

              {/* Progress Bar */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{
                  width: '100%',
                  height: '8px',
                  background: 'var(--border)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${paybackProgress.progressPercent}%`,
                    height: '100%',
                    background: paybackProgress.isPaidOff
                      ? 'linear-gradient(90deg, #10b981, #059669)'
                      : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                    borderRadius: '4px',
                    transition: 'width 0.5s ease'
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontSize: '9px', color: 'var(--muted)' }}>
                  <span>{paybackProgress.progressPercent.toFixed(1)}% {t('fluxNode.nodeCard.recovered', 'recovered')}</span>
                  <span>${paybackProgress.investedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              </div>

              {/* Stats Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', fontSize: '10px' }}>
                <div>
                  <div style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.invested', 'Invested')}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>${paybackProgress.investedUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.monthlyRewardsUSD', 'Monthly')}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>+${paybackProgress.monthlyRewardsUSD.toFixed(0)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.estimatedEarned', 'Est. Earned')}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#8b5cf6' }}>~${paybackProgress.estimatedRewardsEarned.toFixed(0)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--muted)' }}>
                    {paybackProgress.isPaidOff ? t('fluxNode.nodeCard.paybackTime', 'Payback') : t('fluxNode.nodeCard.remaining', 'Remaining')}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: paybackProgress.isPaidOff ? '#10b981' : 'var(--text)'
                  }}>
                    {paybackProgress.isPaidOff ? paybackProgress.paybackFormatted : paybackProgress.remainingFormatted}
                  </div>
                </div>
              </div>

              {/* Tracking Start Date */}
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(99, 102, 241, 0.2)' }} onClick={(e) => e.stopPropagation()}>
                {showDateInput ? (
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="date"
                      value={dateInputValue}
                      onChange={(e) => setDateInputValue(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '10px',
                        background: 'var(--card-bg)',
                        color: 'var(--text)',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        flex: 1,
                        minWidth: '100px'
                      }}
                    />
                    <button
                      onClick={() => {
                        if (dateInputValue && onUpdateTrackingStartDate) {
                          onUpdateTrackingStartDate(dateInputValue);
                        }
                        setShowDateInput(false);
                      }}
                      style={{
                        padding: '4px 8px',
                        fontSize: '9px',
                        fontWeight: 600,
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      OK
                    </button>
                    {paybackProgress.hasCustomDate && (
                      <button
                        onClick={() => {
                          if (onUpdateTrackingStartDate) {
                            onUpdateTrackingStartDate(null);
                            setDateInputValue('');
                          }
                          setShowDateInput(false);
                        }}
                        style={{
                          padding: '4px 8px',
                          fontSize: '9px',
                          fontWeight: 600,
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Reset
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowDateInput(false);
                        setDateInputValue(purchaseInfo?.trackingStartDate || '');
                      }}
                      style={{
                        padding: '4px 8px',
                        fontSize: '9px',
                        fontWeight: 600,
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '9px' }}>
                    <Icons.Calendar size={11} style={{ color: 'var(--muted)' }} />
                    <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.trackingSince', 'Tracking since')}:</span>
                    {paybackProgress.hasCustomDate ? (
                      <button
                        onClick={() => setShowDateInput(true)}
                        style={{
                          padding: '2px 5px',
                          background: 'rgba(99, 102, 241, 0.2)',
                          color: '#6366f1',
                          border: 'none',
                          borderRadius: '3px',
                          fontSize: '9px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {new Date(paybackProgress.trackingStartDate!).toLocaleDateString()}
                      </button>
                    ) : (
                      <>
                        <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                          {t('fluxNode.nodeCard.pnrUptime', 'PNR uptime')}
                        </span>
                        {onUpdateTrackingStartDate && (
                          <button
                            onClick={() => setShowDateInput(true)}
                            style={{
                              padding: '2px 5px',
                              background: 'var(--border)',
                              color: 'var(--muted)',
                              border: 'none',
                              borderRadius: '3px',
                              fontSize: '8px',
                              cursor: 'pointer'
                            }}
                          >
                            {t('fluxNode.nodeCard.setDate', 'Set date')}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Node Info & Hardware */}
          <div
            style={{
              padding: '12px',
              background: 'var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
              marginBottom: '12px'
            }}
          >
            {/* FluxOS & Location */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div>
                <span style={{ color: 'var(--muted)' }}>FluxOS:</span>{' '}
                <span style={{ color: '#10b981', fontWeight: 600 }}>
                  {nodeInfo?.fluxVersion || 'Loading...'}
                </span>
              </div>
              <div>
                {nodeInfo?.countryCode && (
                  <span title={`${nodeInfo.city ? nodeInfo.city + ', ' : ''}${nodeInfo.country}`}>
                    {getCountryFlag(nodeInfo.countryCode)} {nodeInfo.country}
                  </span>
                )}
              </div>
            </div>

            {/* Hardware Specs */}
            {nodeInfo && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '8px' }}>
                <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(99, 102, 241, 0.1)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#6366f1' }}>{nodeInfo.cores}</div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.cpuCores', 'CPU Cores')}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#8b5cf6' }}>{nodeInfo.ram} GB</div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.ram', 'RAM')}</div>
                </div>
                <div style={{ textAlign: 'center', padding: '6px', background: 'rgba(236, 72, 153, 0.1)', borderRadius: '6px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: '#ec4899' }}>{nodeInfo.ssd} GB</div>
                  <div style={{ fontSize: '9px', color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hardware.ssd', 'SSD')}</div>
                </div>
              </div>
            )}

            {/* Uptime & Network */}
            {nodeInfo && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                <div>
                  <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.uptime', 'Uptime')}:</span>{' '}
                  <span style={{ color: '#10b981', fontWeight: 600 }}>
                    {nodeInfo.uptimeSeconds > 0 ? formatUptime(nodeInfo.uptimeSeconds) : '-'}
                  </span>
                </div>
                <div>
                  {/* API returns speeds directly in Mbps */}
                  <span style={{ color: 'var(--muted)' }}>↓</span>{' '}
                  <span>{nodeInfo.downloadSpeed > 0 ? `${Math.round(nodeInfo.downloadSpeed)} Mbps` : '-'}</span>
                  <span style={{ color: 'var(--muted)', marginLeft: '8px' }}>↑</span>{' '}
                  <span>{nodeInfo.uploadSpeed > 0 ? `${Math.round(nodeInfo.uploadSpeed)} Mbps` : '-'}</span>
                </div>
              </div>
            )}

            {/* EPS & Disk Write */}
            {nodeInfo && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', marginTop: '8px' }}>
                <div>
                  <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.eps', 'EPS')}:</span>{' '}
                  <span style={{ color: '#6366f1', fontWeight: 600 }}>
                    {nodeInfo.cpuBench > 0 ? nodeInfo.cpuBench.toLocaleString() : '-'}
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.diskWrite', 'Disk Write')}:</span>{' '}
                  <span style={{ color: '#8b5cf6', fontWeight: 600 }}>
                    {nodeInfo.ssdWrite > 0 ? `${Math.round(nodeInfo.ssdWrite)} MB/s` : '-'}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* PNR (Progressive Node Rewards) Section */}
          <div
            style={{
              padding: '12px',
              background: pnrData?.pnrEligible
                ? 'rgba(16, 185, 129, 0.1)'
                : 'rgba(245, 158, 11, 0.1)',
              border: `1px solid ${pnrData?.pnrEligible
                ? 'rgba(16, 185, 129, 0.3)'
                : 'rgba(245, 158, 11, 0.3)'}`,
              borderRadius: '8px',
              fontSize: '11px',
              marginBottom: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Icons.Award size={16} style={{ color: pnrData?.pnrEligible ? '#10b981' : '#f59e0b' }} />
              <span style={{ fontWeight: 600, color: pnrData?.pnrEligible ? '#10b981' : '#f59e0b' }}>
                {t('fluxNode.pnr.title', 'Progressive Node Rewards (PNR)')}
              </span>
            </div>

            {isPnrLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--muted)' }}>
                <Icons.Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                {t('fluxNode.pnr.loading', 'Chargement...')}
              </div>
            ) : pnrData ? (
              <>
                {/* System Uptime Progress */}
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ color: 'var(--muted)' }}>
                      {t('fluxNode.pnr.systemUptime', 'Uptime Système')}:
                    </span>
                    <span style={{ fontWeight: 600 }}>{pnrData.uptimeFormatted}</span>
                  </div>
                  <div
                    style={{
                      width: '100%',
                      height: '8px',
                      background: 'var(--border)',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        width: `${pnrData.uptimePercent}%`,
                        height: '100%',
                        background: pnrData.pnrEligible
                          ? 'linear-gradient(90deg, #10b981, #059669)'
                          : 'linear-gradient(90deg, #f59e0b, #d97706)',
                        borderRadius: '4px',
                        transition: 'width 0.3s'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: 'var(--muted)' }}>
                    <span>{pnrData.uptimePercent}% {t('fluxNode.pnr.of30Days', 'de 30 jours')}</span>
                    <span>{t('fluxNode.pnr.requirement', 'Requis')}: 30 {t('fluxNode.pnr.days', 'jours')}</span>
                  </div>
                </div>

                {/* Eligibility Status */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    background: pnrData.pnrEligible
                      ? 'rgba(16, 185, 129, 0.2)'
                      : 'rgba(245, 158, 11, 0.2)',
                    borderRadius: '6px'
                  }}
                >
                  {pnrData.pnrEligible ? (
                    <>
                      <Icons.CheckCircle size={16} style={{ color: '#10b981' }} />
                      <span style={{ color: '#10b981', fontWeight: 600 }}>
                        {t('fluxNode.pnr.eligible', 'Éligible aux récompenses PNR')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Icons.Clock size={16} style={{ color: '#f59e0b' }} />
                      <span style={{ color: '#f59e0b', fontWeight: 600 }}>
                        {t('fluxNode.pnr.notYetEligible', 'Encore')} {pnrData.daysUntilEligible} {t('fluxNode.pnr.daysRemaining', 'jours pour éligibilité')}
                      </span>
                    </>
                  )}
                </div>

                {/* ArcaneOS Notice */}
                <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Icons.AlertCircle size={12} />
                  {t('fluxNode.pnr.arcaneOsRequired', 'ArcaneOS requis pour PNR. Nodes legacy non éligibles.')}
                </div>
              </>
            ) : (
              <div style={{ color: 'var(--muted)' }}>
                {t('fluxNode.pnr.unavailable', 'Données PNR non disponibles')}
              </div>
            )}
          </div>

          {/* Latest Benchmarks */}
          <div
            style={{
              padding: '12px',
              background: 'var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
              marginBottom: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Icons.Activity size={14} />
              <span style={{ fontWeight: 600 }}>
                {t('fluxNode.nodeCard.latestBenchmarks', 'Latest Benchmarks')}
              </span>
              {latestBenchmarks?.benchmarkTier && (
                <span style={{
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: 'rgba(99, 102, 241, 0.2)',
                  color: '#6366f1',
                  fontSize: '9px',
                  fontWeight: 700
                }}>
                  {latestBenchmarks.benchmarkTier}
                </span>
              )}
            </div>

            {latestBenchmarks ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', fontSize: '10px' }}>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.eps', 'EPS')}:</span>{' '}
                    <span style={{ color: '#6366f1', fontWeight: 600 }}>{latestBenchmarks.eps.toLocaleString()}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>Ping:</span>{' '}
                    <span style={{ fontWeight: 600 }}>{latestBenchmarks.ping > 0 ? `${latestBenchmarks.ping} ms` : '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>↓ Download:</span>{' '}
                    <span style={{ fontWeight: 600 }}>{(latestBenchmarks.download > 0 ? Math.round(latestBenchmarks.download) : nodeInfo?.downloadSpeed && nodeInfo.downloadSpeed > 0 ? Math.round(nodeInfo.downloadSpeed) : 0) > 0 ? `${latestBenchmarks.download > 0 ? Math.round(latestBenchmarks.download) : Math.round(nodeInfo?.downloadSpeed || 0)} Mbps` : '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>↑ Upload:</span>{' '}
                    <span style={{ fontWeight: 600 }}>{(latestBenchmarks.upload > 0 ? Math.round(latestBenchmarks.upload) : nodeInfo?.uploadSpeed && nodeInfo.uploadSpeed > 0 ? Math.round(nodeInfo.uploadSpeed) : 0) > 0 ? `${latestBenchmarks.upload > 0 ? Math.round(latestBenchmarks.upload) : Math.round(nodeInfo?.uploadSpeed || 0)} Mbps` : '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.diskWrite', 'Disk Write')}:</span>{' '}
                    <span style={{ color: '#8b5cf6', fontWeight: 600 }}>{latestBenchmarks.ddWrite > 0 ? `${Math.round(latestBenchmarks.ddWrite)} MB/s` : '-'}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.totalStorage', 'Storage')}:</span>{' '}
                    <span style={{ fontWeight: 600 }}>{latestBenchmarks.totalStorage > 0 ? `${latestBenchmarks.totalStorage} GB` : '-'}</span>
                  </div>
                </div>
                {latestBenchmarks.time > 0 && (
                  <div style={{ marginTop: '6px', fontSize: '9px', color: 'var(--muted)' }}>
                    {t('fluxNode.nodeCard.benchmarkTime', 'Last benchmark')}: {new Date(latestBenchmarks.time * 1000).toLocaleString()}
                  </div>
                )}
              </>
            ) : (
              <div style={{ color: 'var(--muted)' }}>{t('common.loading', 'Loading...')}</div>
            )}
          </div>

          {/* Apps */}
          <div
            style={{
              padding: '12px',
              background: 'var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
              marginBottom: '12px'
            }}
          >
            <div style={{ marginBottom: '6px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.hostedApps', 'Hosted Apps')}:</span>{' '}
              <span style={{ color: '#8b5cf6', fontWeight: 600 }}>
                {nodeApps ? `${nodeApps.count} app${nodeApps.count !== 1 ? 's' : ''}` : t('common.loading', 'Loading...')}
              </span>
            </div>
            {nodeApps && nodeApps.count > 0 && (
              <div style={{ marginLeft: '8px' }}>
                {nodeApps.apps.slice(0, 5).map((app, idx) => (
                  <div key={idx} style={{ marginBottom: '3px' }}>
                    <a
                      href={fluxNodeAPI.buildAppUrl(app.name, node.ip.split(':')[0])}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#6366f1', textDecoration: 'none' }}
                    >
                      • {app.name}
                    </a>
                  </div>
                ))}
                {nodeApps.count > 5 && (
                  <div style={{ color: 'var(--muted)', fontStyle: 'italic' }}>
                    +{nodeApps.count - 5} {t('fluxNode.nodeCard.more', 'more')}...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Technical Details */}
          <div
            style={{
              padding: '12px',
              background: 'var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
              fontFamily: 'monospace'
            }}
          >
            <div style={{ marginBottom: '6px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.txHash')}:</span>{' '}
              <span style={{ color: 'var(--text)' }}>{node.txhash.substring(0, 20)}...</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.paymentAddress')}:</span>{' '}
              <span style={{ color: 'var(--text)' }}>{node.payment_address.substring(0, 20)}...</span>
            </div>
            <div style={{ marginBottom: '6px' }}>
              <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.confirmedHeight')}:</span>{' '}
              <span style={{ color: 'var(--text)' }}>{node.confirmed_height.toLocaleString()}</span>
            </div>
            <div>
              <span style={{ color: 'var(--muted)' }}>{t('fluxNode.nodeCard.lastPaidHeight')}:</span>{' '}
              <span style={{ color: 'var(--text)' }}>{node.last_paid_height.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
